import React from "react";
import { Video, Calendar, Clock, ExternalLink } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
function fmt(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return `${d.getDate()} de ${MONTHS[d.getMonth()]}`;
}

const STAGE_INFO = [
  { stage: "embarazo", title: "Embarazo", hex: "#D4A8B0" },
  { stage: "puerperio", title: "Puerperio", hex: "#C4705A" },
  { stage: "menopausia", title: "Menopausia", hex: "#7A5C9E" },
];

export default function Sesiones() {
  const { sessions } = useStore();
  const { user, isAdmin } = useAuth();

  const visibleStages = isAdmin || !user?.stage ? STAGE_INFO : STAGE_INFO.filter((s) => s.stage === user.stage);

  const latestForStage = (stage) => {
    const list = sessions.filter((s) => s.stage === stage);
    if (!list.length) return null;
    return list.slice().sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-gradient-to-b from-amber/30 to-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">En directo por Zoom</span>
          <h1 className="font-serif text-5xl md:text-6xl text-plum mt-3">Sesiones grupales</h1>
          <p className="font-body text-lg text-ink/65 mt-4 max-w-2xl mx-auto">
            Encuentros mensuales en directo para compartir, aprender y sentirte acompañada por Lorena y la comunidad.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-12 space-y-6">
        {visibleStages.map(({ stage, title, hex }) => {
          const s = latestForStage(stage);
          return (
            <div
              key={stage}
              data-testid={`session-card-${stage}`}
              className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border flex flex-col md:flex-row md:items-center gap-6"
              style={{ borderColor: "rgba(232,196,154,0.4)", borderLeftWidth: 4, borderLeftColor: hex }}
            >
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-cream shrink-0" style={{ backgroundColor: hex }}>
                <Video size={30} />
              </div>
              <div className="flex-1">
                <span className="font-sans text-[11px] uppercase tracking-[0.15em]" style={{ color: hex }}>{title}</span>
                {s ? (
                  <>
                    <h3 className="font-serif text-2xl text-plum mt-1">{s.title}</h3>
                    <p className="font-body text-ink/65 text-sm mt-1">{s.description}</p>
                    <div className="flex flex-wrap gap-4 mt-3 font-body text-sm text-ink/55">
                      <span className="flex items-center gap-1.5"><Calendar size={15} className="text-terracotta" /> {fmt(s.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={15} className="text-terracotta" /> {s.time} · {s.duration}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-serif text-2xl text-plum mt-1">Próximo encuentro</h3>
                    <p className="font-body text-ink/55 text-sm mt-1">Aún no hay fecha programada. ¡Muy pronto habrá un nuevo encuentro!</p>
                  </>
                )}
              </div>
              {s && (
                <a
                  href={s.zoomLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`session-join-${stage}`}
                  className="inline-flex items-center justify-center gap-2 text-cream font-sans px-6 py-3 rounded-full transition-opacity hover:opacity-90 whitespace-nowrap"
                  style={{ backgroundColor: hex }}
                >
                  Unirme <ExternalLink size={16} />
                </a>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
